import _ from "lodash";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { formatDate } from "../../classes/DateHelper";
import IPortletSettings from "../../classes/PortletSettings";
import { IConfig } from "../../Config";
import DataService from "../../data/DataService";
import { ViewType } from "../../NewsroomView";
import Loader from "../Loader";
import ButtonBar from "./components/ButtonBar";
import Categories from "./components/Categories";
import NavBar from "./components/NavBar";
import NewsFeed from "./components/NewsFeed";
import RecentPosts from "./components/RecentPosts";
import ShareNav from "./components/ShareBar";
import Tags from "./components/Tags";
import Template from "./Template";

interface IProps {
    config: IConfig;
    dataService: DataService;
    newsId: string;
    isAdmin: boolean;
    isViewingArchivedItems: boolean;
    onNewsDeleted: () => void;
    onSwitchView: (view: ViewType, newsId?: string) => void;
    recentArchivedPosts: any[];
    recentNewsPosts: any[];
    settings: IPortletSettings;
    stylesheet: string;
    template: string;
    friendlyUrl: string;
}

const DefaultRecentNewsCount = 5;

const Post = (props: IProps) => {
    const [categories, setCategories] = useState<any[]>([]);
    const [hasNext, setHasNext] = useState<boolean>(false);
    const [hasPrev, setHasPrev] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [news, setNews] = useState<any>();
    const [recentNews, setRecentNews] = useState<any[]>([]);
    const [tags, setTags] = useState<any[]>([]);
    const [feed, setFeed] = useState<any>(null);
    const [isWritable, setIsWritable] = useState(false);

    const loadNews = useCallback(async (friendlyUrl) => {
        const news = await retrieveNews(friendlyUrl);
        const categories = _.uniqBy(_.map(news?.categories, (category) => category?.categoryName), (str) => str.toLowerCase());
        const tags = _.uniqBy(_.map(news?.tags, (tag) => tag?.tag), (str) => str.toLowerCase());
        setFeed(news?.feeds[0]);
        setNews(news);
        setCategories(categories);
        setTags(tags);

        // Item is not writable if its feed is not set to publish for this server.
        // In that case, the edit/archive buttons should not be shown.
        const allWritableFeeds = ((await props.dataService.getAnonymousFeeds(true)).data)?.items || [];
        const writableFeedIds = allWritableFeeds.map((f) => f.feedId);
        setIsWritable(news.feeds.some((f) => writableFeedIds.includes(f.feedId)));
    }, [props.dataService, props.newsId]);

    const retrieveNews = useCallback(async (friendlyUrl) => {
        try {
            const viewArchived = props.isViewingArchivedItems;
            const includeAllData = props.config.role === "superadmin" || props.config.role === "author";
            const news = (await props.dataService.getNewsByFriendlyUrl(friendlyUrl, props.config.feedIds, viewArchived, includeAllData)).data || {};
            return news;
        } catch (ex) {
            return null;
        }
    }, [props.dataService]);

    const loadRecentNews = useCallback(async (friendlyUrl) => {
        const recentNews = (props.isViewingArchivedItems ? props.recentArchivedPosts : props.recentNewsPosts)
            .filter((n) => n.friendlyUrl !== friendlyUrl)
            .slice(0, DefaultRecentNewsCount);
        setRecentNews(recentNews);
    }, [props.dataService, news, props.recentNewsPosts]);

    const title = useCallback((post, cssClass = "", noAnchor = false) => {
        let headingLevel = (props.settings?.HeadingLevel || "").toLowerCase();
        headingLevel = noAnchor && headingLevel === "a" ? "h3" : headingLevel;
        const className = `NR_title CMS_title ${cssClass}`;
        switch (headingLevel) {
            case "a":
                return <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        onReadPost(post.newsId);
                    }}
                >
                    {post.title}
                </a>;
            case "div":
                return <div className={className}>{post.title}</div>;
            case "span":
                return <span className={className}>{post.title}</span>;
            case "h1":
                return <h1 className={className}>{post.title}</h1>;
            case "h2":
                return <h2 className={className}>{post.title}</h2>;
            case "h3":
            default:
                return <h3 className={className}>{post.title}</h3>;
        }
    }, [props.settings]);

    const onDelete = useCallback(async (newsId, isDeleted, isPermanent = false) => {
        try {
            setIsLoading(true);
            if (isDeleted) {
                await props.dataService.deleteNews(newsId, isPermanent);
            } else {
                const news = (await props.dataService.getNews(newsId)).data || {};
                if (news) {
                    news.categories = _.map(news.categories, (o) => o.categoryId);
                    news.tags = _.map(news.tags, (o) => o.tag);
                    news.websites = _.map(news.websites, (o) => o.serverId);
                    news.deleted = false;
                    await props.dataService.updateNews(newsId, news);
                }
            }
        } finally {
            props.onNewsDeleted();
            props.onSwitchView(ViewType.Summary);
            setIsLoading(false);
        }
    }, [props.dataService]);

    const onPrevPost = useCallback(async () => {
        if (hasPrev) {
            props.onSwitchView(ViewType.Details, news.previousFriendlyUrlName);
        }
    }, [hasPrev, news]);

    const onNextPost = useCallback(async () => {
        if (hasNext) {
            props.onSwitchView(ViewType.Details, news.nextFriendlyUrlName);
        }
    }, [hasNext, news]);

    const getRecentPostUrl = useCallback((newsId: string) => {
        const readMode = props.isViewingArchivedItems ? "archived" : "news";
        return `/${props.config.portletInstanceId}/read/${readMode}/${newsId}`;
    }, [history, props.config]);

    const onReadPost = useCallback((friendlyUrl: string) => {
        props.onSwitchView(ViewType.Details, friendlyUrl);
    }, [history, props.config]);

    const init = useCallback(async (friendlyUrl) => {
        try {
            setIsLoading(true);
            await loadNews(friendlyUrl);
            await loadRecentNews(friendlyUrl);
        } finally {
            setIsLoading(false);
        }
    }, [props.dataService]);

    useEffect(() => {
        setHasPrev(!_.isEmpty(news?.previousNewsId));
        setHasNext(!_.isEmpty(news?.nextNewsId));
    }, [news]);

    useEffect(() => {
        (async () => {
            await init(props.friendlyUrl);
        })();
    }, [props.friendlyUrl]);

    return (
        <Loader isLoading={isLoading}>
            {props.settings.LayoutStyle === "template" ? (
                <Template
                    settings={props.settings}
                    recentNewsPosts={(props.isViewingArchivedItems ? props.recentArchivedPosts : props.recentNewsPosts)}
                    isAdmin={props.isAdmin && isWritable}
                    isViewingArchivedItems={props.isViewingArchivedItems}
                    post={news}
                    config={props.config}
                    template={props.template}
                    stylesheet={props.stylesheet}
                    categories={categories}
                    tags={tags}
                    feed={news?.feeds ? news?.feeds[0] : null}
                    showAuthor={props.settings.ShowAuthor}
                    hasPrev={hasPrev}
                    hasNext={hasNext}
                    title={title}
                    getRecentPostUrl={getRecentPostUrl}
                    onBack={() => props.onSwitchView(ViewType.Summary)}
                    onReadRecentPost={onReadPost}
                    onDelete={onDelete}
                    onPrevPost={onPrevPost}
                    onNextPost={onNextPost}
                    onSwitchView={props.onSwitchView}
                />
            ) : (
                <div style={{ maxWidth: "1200px", margin: "50px auto" }}>
                    <div className="CMS_element NR-wrapper NR-viewpost">
                        <div className="CMS_splitSection CMS_contentWSidebar">
                            <div className="CMS_contentArea">
                                <div className="CMS_e_row">
                                    <ButtonBar
                                        config={props.config}
                                        post={news}
                                        isAdmin={props.isAdmin && isWritable}
                                        onSwitchView={props.onSwitchView}
                                        onDelete={onDelete}
                                    />
                                </div>
                                <div className="CMS_e_row">
                                    <div className="NR_posts NR_individual">
                                        {news ?
                                            <div className="NR_item">
                                                {!_.isEmpty(news.featuredImage) ? <div
                                                    className="NR_image"
                                                    aria-label={news.featuredImageCaption}
                                                    style={{ background: `url('${_.trimEnd(props.config.cdnLocation, "/")}${news.featuredImage}') center center / cover no-repeat`, height: "280px" }}
                                                ></div> : null}
                                                {title(news, "CMS_T4", true)}
                                                <div className="NR_publishDate">{formatDate(news.scheduledDate)} {props.settings.ShowAuthor ? <>- by {news.author}</> : null}</div>
                                                <div className="NR_body" dangerouslySetInnerHTML={{ __html: news.body }}></div>
                                            </div> : <p style={{ textAlign: "center" }}>Page not found</p>}
                                    </div>
                                </div>
                                <div className="CMS_e_row">
                                    {!_.isEmpty(categories) || !_.isEmpty(tags) || (news?.feeds !== undefined && !_.isEmpty(feed?.feedName)) ? <div className="CMS_subsection">
                                        <div className="CMS_e_row">
                                            {feed !== undefined && !_.isEmpty(feed.feedName) && props.settings !== undefined && props.settings.ShowFeedName ? <NewsFeed newsfeed={feed.feedName.trim()} /> : null}
                                            <Categories categories={categories} />
                                            <Tags tags={tags} />
                                        </div>
                                    </div> : null}
                                </div>
                                <div className="CMS_e_row">
                                    <NavBar hasPrev={hasPrev} hasNext={hasNext} onPrevPost={onPrevPost} onNextPost={onNextPost} />
                                </div>
                            </div>
                            <div className="CMS_sidebar">
                                <ShareNav config={props.config} post={news} />
                                <RecentPosts
                                    config={props.config}
                                    settings={props.settings}
                                    recentNews={recentNews}
                                    getRecentPostUrl={getRecentPostUrl}
                                    onReadRecentPost={onReadPost}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Loader>
    );
};

export default Post;
